import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const CHAPTERS = [
  {
    n: "01",
    title: "Conviction over consensus",
    body: "We take concentrated positions in the businesses we understand deeply — backing outlier operators long before the market agrees.",
  },
  {
    n: "02",
    title: "Build, then compound",
    body: "Capital is only the beginning. We partner operationally, engineer durability, and let disciplined businesses compound across cycles.",
  },
  {
    n: "03",
    title: "A multi-decade horizon",
    body: "We invest as owners, not tourists — measuring returns in generations of value rather than quarters of noise.",
  },
];

const RevealWord = ({ children }) => (
  <span className="inline-block overflow-hidden align-bottom">
    <motion.span
      variants={{
        hidden: { y: "110%" },
        show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
      }}
      className="inline-block"
    >
      {children}
    </motion.span>
  </span>
);

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const lineScale = useTransform(scrollYProgress, [0.1, 0.6], [0, 1]);

  const words = "We invest with intent.".split(" ");

  return (
    <section
      ref={ref}
      data-testid="manifesto-section"
      className="relative py-16 lg:py-24 border-t border-[#E0DDD6]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-16">
          <span className="inline-block w-8 h-px bg-[#136207]" />
          <span className="overline text-[#136207]">01 · Manifesto</span>
        </div>

        {/* Big kinetic statement */}
        <motion.h2
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-120px" }}
          transition={{ staggerChildren: 0.08 }}
          className="font-display text-[#1A1A1A] text-[38px] sm:text-6xl lg:text-8xl tracking-tight leading-[0.98] font-medium max-w-5xl"
        >
          {words.map((w, i) => (
            <span key={i} className="mr-3 inline-block">
              <RevealWord>
                <span className={i === words.length - 1 ? "text-[#136207]" : ""}>
                  {w}
                </span>
              </RevealWord>
            </span>
          ))}
        </motion.h2>

        {/* Numbered chapters */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E0DDD6] border border-[#E0DDD6]">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="group relative bg-[#FFFFFF]/70 backdrop-blur p-9 lg:p-11 min-h-[300px] flex flex-col justify-between hover:bg-[#EFEDE8]/85 transition-colors"
            >
              <div className="flex items-start justify-between">
                <span className="font-display text-[#136207] text-5xl lg:text-6xl leading-none">
                  {c.n}
                </span>
                <span className="w-8 h-8 border border-[#E0DDD6] group-hover:border-[#136207] flex items-center justify-center transition-colors">
                  <span className="w-1.5 h-1.5 bg-[#136207] rounded-full" />
                </span>
              </div>
              <div className="mt-12">
                <h3 className="font-display text-[#1A1A1A] text-2xl lg:text-3xl tracking-tight leading-tight">
                  {c.title}
                </h3>
                <p className="mt-4 text-[#5C5A55] text-sm lg:text-[15px] leading-relaxed font-light">
                  {c.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Animated underline */}
        <motion.div
          style={{ scaleX: lineScale }}
          className="mt-12 h-px w-full bg-gradient-to-r from-[#136207] via-[#136207] to-transparent origin-left"
        />
      </div>
    </section>
  );
};

export const Marquee = () => {
  const items = [
    "Technology",
    "Energy",
    "Resources",
    "Infrastructure & Mining",
    "Fintech",
    "Agritech",
    "Space",
    "AI",
  ];
  const row = [...items, ...items];
  return (
    <section
      data-testid="marquee-section"
      className="relative py-10 border-y border-[#E0DDD6] overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-[#F7F6F3] to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-[#F7F6F3] to-transparent z-10" />
      <div
        className="flex whitespace-nowrap"
        style={{ animation: "marquee 45s linear infinite", width: "max-content" }}
      >
        {row.map((t, i) => (
          <span key={i} className="flex items-center">
            <span className="font-display text-[#1A1A1A]/90 text-4xl sm:text-6xl px-8 tracking-tight">
              {t}
            </span>
            <span className="text-[#136207] text-4xl sm:text-5xl px-2">/</span>
          </span>
        ))}
      </div>
    </section>
  );
};

export default Manifesto;
